import { z } from 'zod';

/**
 * The units a duration setting can be **stored** in, smallest first.
 *
 * Every one is a fixed number of seconds, and every larger one is a whole
 * multiple of every smaller one — the invariant that lets the admin offer
 * "minutes" over a value held in seconds and convert both ways without ever
 * landing between two integers. Months and years are deliberately absent: their
 * length varies, so "1 month" is a calendar operation rather than a duration,
 * and a TTL expressed in one cannot be enforced.
 */
export const DURATION_UNITS = ['seconds', 'minutes', 'hours', 'days'] as const;

export const DurationUnitSchema = z.enum(DURATION_UNITS);

export type DurationUnit = (typeof DURATION_UNITS)[number];

/** How many seconds one of each unit is. */
export const DURATION_UNIT_SECONDS: Readonly<Record<DurationUnit, number>> = Object.freeze({
  seconds: 1,
  minutes: 60,
  hours: 60 * 60,
  days: 24 * 60 * 60,
});

export function isDurationUnit(value: string): value is DurationUnit {
  return DurationUnitSchema.safeParse(value).success;
}
